import {
  lowerBound,
  smoothSeries,
  trackTimeSegments,
} from '@shared/geoutils.js';
import type { Feature, LineString } from 'geojson';
import type { ColorizerMessages } from './translations/ColorizerMessages.js';

export type HotlinePalette = Array<{
  r: number;
  g: number;
  b: number;
  t: number;
}>;

/**
 * The Hotline options a colorizer imposes, taken from the same place as the
 * palette so a new caller cannot forget them: a span-based mode changes color
 * across coincident points, which Leaflet's simplification would drop.
 */
export function colorizerHotlineOptions(
  colorizer: Colorizer | null | undefined,
): { palette: HotlinePalette | undefined; smoothFactor?: number } {
  return {
    palette: colorizer?.palette,
    ...(colorizer?.spanBased && { smoothFactor: 0 }),
  };
}

/**
 * The geometry a colorizer should read: a span-based mode takes the router's own
 * values off the plain line, where every other mode wants the densified one the
 * elevation pipeline built.
 */
export function colorizeGeometrySource<T>(
  colorizer: Colorizer | null | undefined,
  densified: T | null | undefined,
): T | null {
  return !colorizer || colorizer.spanBased ? null : (densified ?? null);
}

/** A palette color as CSS, the one spelling both the line and the legend use. */
export function rgbCss(color: [r: number, g: number, b: number]): string {
  return `rgb(${color.join(' ')})`;
}

/**
 * A stretch of a route the router reported one value for, in metres along the
 * line. Metres rather than point indices because the line colorize draws is
 * densified for premium users, which shifts every index.
 */
export type PathDetailSpan = { start: number; end: number; value: string };

export type PathDetails = Record<string, PathDetailSpan[]>;

/** Where {@link PathDetailSpan}s ride on the feature being colorized. */
export const PATH_DETAILS_PROP = 'fm:pathDetails';

export function readPathDetails(
  feature: Feature<LineString>,
  key: string,
): PathDetailSpan[] | undefined {
  const details = feature.properties?.[PATH_DETAILS_PROP] as
    | PathDetails
    | undefined;

  return details?.[key];
}

/** The spans covering `[from, to]`, measured from `from`. */
export function clipPathDetails(
  details: PathDetails,
  from: number,
  to: number,
): PathDetails {
  return Object.fromEntries(
    Object.entries(details).flatMap(([key, spans]) => {
      const clipped: PathDetailSpan[] = [];

      for (const span of spans) {
        const start = Math.max(span.start, from);

        const end = Math.min(span.end, to);

        if (end > start) {
          clipped.push({
            start: start - from,
            end: end - from,
            value: span.value,
          });
        }
      }

      return clipped.length > 0 ? [[key, clipped] as const] : [];
    }),
  );
}

/**
 * The same spans read off another measure of the same vertices: `from` and `to`
 * hold one distance per vertex, each along its own axis, and a span between two
 * vertices is interpolated. Simplifying a line and joining two both move every
 * vertex along the axis the spans are metres of.
 *
 * A gap an axis leaves out is two vertices at the same distance, so a span
 * starting there takes the later of them and one ending there the earlier —
 * both taking the same one would stretch the span across the gap.
 */
export function remapPathDetails(
  details: PathDetails,
  from: number[],
  to: number[],
): PathDetails {
  const last = from.length - 1;

  const at = (meters: number, starting: boolean) => {
    if (last < 0) {
      return meters;
    }

    const i = lowerBound(from.length, (index) =>
      starting ? from[index]! > meters : from[index]! >= meters,
    );

    if (i <= 0) {
      return to[0]!;
    }

    if (i > last) {
      return to[last]!;
    }

    const width = from[i]! - from[i - 1]!;

    const f = width > 0 ? (meters - from[i - 1]!) / width : 0;

    return to[i - 1]! + f * (to[i]! - to[i - 1]!);
  };

  return Object.fromEntries(
    Object.entries(details).map(([key, spans]) => [
      key,
      spans.map((span) => ({
        ...span,
        start: at(span.start, true),
        end: at(span.end, false),
      })),
    ]),
  );
}

/** One row of a legend that names categories instead of drawing a scale. */
export type CategoryShare = {
  key: string;
  label: string;
  color: string;
  /** How far it runs, so the legend doubles as what the route is made of. */
  meters: number;
};

export interface ColorizedPoint {
  lat: number;
  lon: number;
  color: number;
  // True when the underlying value is missing. The colorized line breaks at
  // such points instead of drawing through a misleading mid-palette color.
  gap?: boolean;
}

// {@link noDataRuns} stretches a colorizer can't value are drawn as a neutral,
// slightly thinner, semi-transparent line: it reads as "no value here" and
// stays distinct from a full-strength colored run by form rather than hue, so
// it never collides with a palette color (e.g. the gray of `elevation`).
export const NO_DATA_COLOR = '#808080';
export const NO_DATA_OPACITY = 2 / 3;

/**
 * Split colorized points into contiguous runs, breaking at gaps (points whose
 * value is missing). Runs shorter than two points can't form a line and are
 * dropped, leaving a hole the {@link noDataRuns} fill spans in a neutral color.
 */
export function splitOnGaps(points: ColorizedPoint[]): ColorizedPoint[][] {
  const runs: ColorizedPoint[][] = [];

  let current: ColorizedPoint[] = [];

  for (const point of points) {
    if (point.gap) {
      if (current.length > 1) {
        runs.push(current);
      }

      current = [];
    } else {
      current.push(point);
    }
  }

  if (current.length > 1) {
    runs.push(current);
  }

  return runs;
}

/**
 * The complement of {@link splitOnGaps}: runs covering every edge that touches
 * a gap, including the valid points bordering each gap so the run connects to
 * the colorized line. Drawn in {@link NO_DATA_COLOR} so stretches the colorizer
 * has no value for stay visible instead of leaving a hole.
 */
export function noDataRuns(points: ColorizedPoint[]): ColorizedPoint[][] {
  const runs: ColorizedPoint[][] = [];

  let current: ColorizedPoint[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    if (points[i]!.gap || points[i + 1]!.gap) {
      if (current.length === 0) {
        current.push(points[i]!);
      }

      current.push(points[i + 1]!);
    } else if (current.length > 0) {
      runs.push(current);

      current = [];
    }
  }

  if (current.length > 0) {
    runs.push(current);
  }

  return runs;
}

export interface ColorizeOptions {
  // Current integer map zoom. Value-smoothing windows widen when zoomed out so
  // sub-pixel wiggle in the source data doesn't read as color noise; omitted
  // (e.g. export, tests) the colorizer keeps its intrinsic baseline span.
  zoom?: number;
  // The grade the steepness palette ends at, as a ratio; omitted, its default.
  steepnessScale?: number;
}

export interface Colorizer {
  palette: HotlinePalette;
  isAvailable?: (features: Feature<LineString>[]) => boolean;
  compute: (
    features: Feature<LineString>[],
    options?: ColorizeOptions,
  ) => ColorizedPoint[][];
  // Derived from the elevation coordinate, so it benefits from the same
  // fill/override prompt the elevation chart uses.
  needsElevation?: boolean;
  // Describes the legend for a per-feature-normalized scalar mode: its unit and
  // the smoothed per-point series the colorizer maps to color, so the legend can
  // label real min/max from the same numbers (not raw). Co-located with the
  // colorizer that owns the unit and source field. Absent for fixed-scale
  // (steepness, battery, GSM), categorical (heading), and time modes.
  legend?: {
    unit: string;
    values: (
      feature: Feature<LineString>,
      options?: ColorizeOptions,
    ) => number[];
  };
  // The legend for a mode that names categories instead of drawing a scale: the
  // ones the lines actually hold, with how far each runs. The colorizer answers
  // it, so the legend never has to know how a mode stores its values.
  categories?: (
    features: Feature<LineString>[],
    messages: ColorizerMessages,
  ) => CategoryShare[];
  // Set by a mode that paints stretches the router reported rather than a value
  // per point. Such a line changes color across two coincident points, so
  // Leaflet's vertex simplification has to be off for it (`smoothFactor`); it
  // reads nothing from the elevation-densified line, and its result is the same
  // at every zoom.
  spanBased?: true;
}

/**
 * The smoothed per-point series {@link colorizeByValues} normalizes against. The
 * legend reads these (not the raw samples) so its labels match the colored line.
 */
export function smoothedValues(spec: {
  coords: number[][];
  values: number[];
  smoothSpan?: number;
}): number[] {
  return smoothSeries(spec.coords, spec.values, spec.smoothSpan ?? 0);
}

/**
 * Build positions by normalizing per-coord values to 0..1 via min/max across
 * each feature. A `smoothSpan` (metres) low-passes the values along the path
 * before normalizing — the shared zoom-aware generalization every scalar mode
 * uses. NaN inputs are flagged as gaps from the *original* samples (the line
 * breaks there); they keep the mid-palette color (0.5) only as a harmless
 * filler, which is also the fallback when the value range is degenerate.
 * Keeping the Hotline input within [0, 1] avoids CanvasGradient crashes.
 */
export function colorizeByValues(
  features: Feature<LineString>[],
  perFeature: (feature: Feature<LineString>) => {
    coords: number[][];
    values: number[];
    smoothSpan?: number;
    // Fixed `[min, max]` for an absolute scale, with the color clamped into it
    // so it means the same across tracks; omitted, values normalize against the
    // feature's own smoothed min/max.
    range?: [number, number];
  },
): ColorizedPoint[][] {
  return features.map((feature) => {
    const {
      coords,
      values,
      smoothSpan,
      range: fixedRange,
    } = perFeature(feature);

    const smoothed = smoothSeries(coords, values, smoothSpan ?? 0);

    let min: number;

    let max: number;

    if (fixedRange) {
      [min, max] = fixedRange;
    } else {
      const valid = smoothed.filter((v): v is number => Number.isFinite(v));

      min = valid.length ? Math.min(...valid) : 0;

      max = valid.length ? Math.max(...valid) : 0;
    }

    const range = max - min;

    return coords.map((coord, i) => {
      const v = smoothed[i];

      // Gaps come from the original sample, not the smoothed value, which may
      // have carried a neighbor forward across a hole.
      const gap = !Number.isFinite(values[i]);

      const color =
        range > 0 && Number.isFinite(v)
          ? Math.max(0, Math.min(1, (v - min) / range))
          : 0.5;

      return { lat: coord[1], lon: coord[0], color, gap };
    });
  });
}

export function readNumericArray(
  feature: Feature<LineString>,
  key: string,
  expectedLength: number,
): number[] | null {
  const cp = feature.properties?.['coordinateProperties'] as
    | Record<string, unknown>
    | undefined;

  const raw = cp?.[key];

  if (!Array.isArray(raw) || raw.length !== expectedLength) {
    return null;
  }

  // togeojson fills the array with `null` and writes values where present;
  // map them to NaN so colorizeByValues filters them out of the range.
  return raw.map((v) => (typeof v === 'number' ? v : NaN));
}

export function hasNumericArray(
  features: Feature<LineString>[],
  key: string,
): boolean {
  return features.some((f) => {
    const arr = readNumericArray(f, key, f.geometry.coordinates.length);

    return arr?.some((v) => Number.isFinite(v));
  });
}

/**
 * Reads per-point timestamps as epoch millis. togeojson puts GPX/KML times under
 * `coordinateProperties.times`; live tracking writes a top-level `coordTimes`.
 * Returns null unless an array of the expected length is present; bad entries
 * become NaN.
 */
export function readCoordTimes(
  feature: Feature<LineString>,
  expectedLength: number,
): number[] | null {
  // A colorized line is a single `LineString`, so its times are one segment.
  const segments = trackTimeSegments(feature);

  const raw = segments.length === 1 ? segments[0]! : segments.flat();

  if (raw.length !== expectedLength) {
    return null;
  }

  return raw.map((t) => (typeof t === 'string' ? new Date(t).getTime() : NaN));
}
